import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Card, CardContent, Typography, Box, Link, Button, Divider, IconButton } from '@mui/material';
import { ExpandMore, ExpandLess, ContentCopy, Edit, OpenInNew } from '@mui/icons-material';
import useFormatDate from 'src/hooks/useFormatDate';
import { useDecodedToken } from 'src/hooks/useDecodedToken';
import useUsers from 'src/hooks/useUsers';
import ClaimButton from 'src/components/ClaimButton';
import QueryStatus from 'src/pages/QueryPages/components/QueryStatus';
import UserSelect from 'src/pages/QueryPages/components/UserSelect';
import { toast } from 'react-hot-toast';
import OutletDetails from './OutletDetails';
import { useExpertManagement } from 'src/hooks/useExpertManagement';
import QueryFormModal from 'src/components/QueryFormModal';
import { QUERY_SOURCES } from 'src/constants/config';
import CompanySelect from 'src/pages/QueryPages/components/CompanySelect';
const QueryDetailsCard = ({ query, handleDiscardQuery, refetch }) => {
  const { users } = useUsers();
  const { isExpert } = useDecodedToken();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const { selectedExpert } = useExpertManagement(query);

  const claimedUser = users?.find((user) => user?.id === query?.userClaimID);

  const splitCamelCase = (str) => {
    if (typeof str !== 'string') return '';
    return str.replace(/([a-z])([A-Z])/g, '$1 $2');
  };

  const getFirstTopic = (topics) => {
    if (!topics) return 'No topic';
    const parsedTopics = JSON.parse(topics);
    return parsedTopics.length > 0 ? splitCamelCase(parsedTopics[0]) : 'No topic';
  };

  function getLinkType(url) {
    if (url.includes('connectively')) {
      return 'Connectively';
    } else if (url.includes('qwoted')) {
      return 'Qwoted';
    } else {
      return 'Unknown';
    }
  }

  const [expanded, setExpanded] = React.useState(false);
  const descriptionRef = React.useRef(null);

  const toggleDescription = () => {
    setExpanded(!expanded);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(query.description);
    toast.success('Description copied to clipboard');
  };
  return (
    <>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <Typography variant="body1" sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap', fontWeight: 'bold', flex: 1 }}>
              {query.title}
            </Typography>
            {!isExpert && (
              <IconButton onClick={() => setEditModalOpen(true)} size="small" sx={{ ml: 1 }} color="primary">
                <Edit />
              </IconButton>
            )}
          </Box>

          {!isExpert && query.status !== 'draft' && query.status !== 'new' && (
            <>
              <Divider sx={{ my: 2, borderColor: 'rgba(0, 0, 0, 0.1)' }} />

              <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: { xs: 'column', sm: 'row' } }}>
                <Typography
                  variant="body1"
                  sx={{
                    minWidth: { xs: '100%', sm: 150 },
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    fontWeight: 'bold',
                    mb: { xs: 1, sm: 0 },
                  }}
                >
                  Status:
                </Typography>
                <QueryStatus query={query} mutate={refetch} size="medium" />
              </Box>

              {(query.status === 'successful' || query.status === 'pending') && query.mediaURL && (
                <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: { xs: 'column', sm: 'row' }, mt: 2 }}>
                  <Typography
                    variant="body1"
                    sx={{
                      minWidth: { xs: '100%', sm: 150 },
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      fontWeight: 'bold',
                      mb: { xs: 1, sm: 0 },
                    }}
                  >
                    Publication:
                  </Typography>
                  <Typography variant="body1" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    <Button variant="contained" color="primary" href={query.mediaURL} target="_blank" rel="noopener noreferrer">
                      View
                    </Button>
                  </Typography>
                </Box>
              )}
            </>
          )}

          {query.status === 'draft' && (
            <>
              <Divider sx={{ my: 2, borderColor: 'rgba(0, 0, 0, 0.1)' }} />
              <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: { xs: 'column', sm: 'row' } }}>
                <Typography
                  variant="body1"
                  sx={{
                    minWidth: { xs: '100%', sm: 150 },
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    fontWeight: 'bold',
                    mb: { xs: 1, sm: 0 },
                  }}
                >
                  Company:
                </Typography>
                <Box sx={{ flex: 1, width: '100%' }}>
                  <CompanySelect query={query} mutate={refetch} />
                </Box>
              </Box>
            </>
          )}

          {selectedExpert && (
            <>
              <Divider sx={{ my: 2, borderColor: 'rgba(0, 0, 0, 0.1)' }} />
              <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: { xs: 'column', sm: 'row' } }}>
                <Typography
                  variant="body1"
                  sx={{
                    minWidth: { xs: '100%', sm: 150 },
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    fontWeight: 'bold',
                    mb: { xs: 1, sm: 0 },
                  }}
                >
                  Expert:
                </Typography>
                {/* <Avatar src={selectedExpert.imageURL} sx={{ width: 40, height: 40, mr: 2 }} /> */}
                <Typography variant="body1" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  {selectedExpert.name}

                  <br />

                  <span style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>
                    ({selectedExpert.position} at {selectedExpert.company})
                  </span>
                </Typography>
              </Box>
            </>
          )}

          <Divider sx={{ my: 2, borderColor: 'rgba(0, 0, 0, 0.1)' }} />

          <Box sx={{ display: 'flex', alignItems: 'flex-start', flexDirection: { xs: 'column', sm: 'row' } }}>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="body1"
                ref={descriptionRef}
                sx={{
                  position: 'relative',
                  maxHeight: expanded ? 'none' : { xs: '12em', sm: '19.5em' },
                  overflow: 'hidden',
                  transition: 'max-height 0.3s ease-out',
                  wordBreak: 'break-word',
                  whiteSpace: 'pre-wrap',
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                }}
              >
                {query.description}
              </Typography>
              {query.description && query.description.length > 290 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Button
                    onClick={toggleDescription}
                    sx={{
                      textTransform: 'none',
                      mt: 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      color: 'primary.main',
                      px: 0,
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                    }}
                  >
                    {expanded ? (
                      <>
                        Show Less <ExpandLess sx={{ ml: 0.5 }} />
                      </>
                    ) : (
                      <>
                        Show More <ExpandMore sx={{ ml: 0.5 }} />
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={copyToClipboard}
                    sx={{
                      minWidth: 'auto',
                      mt: 0.5,
                      p: 1,
                    }}
                  >
                    <ContentCopy />
                  </Button>
                </Box>
              )}
            </Box>
          </Box>

          <Divider sx={{ my: 2, borderColor: 'rgba(0, 0, 0, 0.1)' }} />

          <Box sx={{ display: 'flex', alignItems: 'flex-start', flexDirection: { xs: 'column', sm: 'row' } }}>
            <Typography
              variant="body1"
              sx={{
                minWidth: { xs: '100%', sm: 150 },
                fontSize: { xs: '0.875rem', sm: '1rem' },
                fontWeight: 'bold',
                mb: { xs: 1, sm: 0 },
              }}
            >
              Industry:
            </Typography>
            <Typography variant="body1" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
              {getFirstTopic(query.topics)}
            </Typography>
          </Box>

          <Divider sx={{ my: 2, borderColor: 'rgba(0, 0, 0, 0.1)' }} />

          <OutletDetails query={query} />

          <Divider sx={{ my: 2, borderColor: 'rgba(0, 0, 0, 0.1)' }} />

          <Box sx={{ display: 'flex', alignItems: 'flex-start', flexDirection: { xs: 'column', sm: 'row' } }}>
            <Typography
              variant="body1"
              sx={{
                minWidth: { xs: '100%', sm: 150 },
                fontSize: { xs: '0.875rem', sm: '1rem' },
                fontWeight: 'bold',
                mb: { xs: 1, sm: 0 },
              }}
            >
              Deadline:
            </Typography>
            <Typography variant="body1" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
              {useFormatDate(query.dueDate) || 'No deadline'}
            </Typography>
          </Box>

          {!isExpert && (
            <>
              <Divider sx={{ my: 2, borderColor: 'rgba(0, 0, 0, 0.1)' }} />

              <Box sx={{ display: 'flex', alignItems: 'flex-start', flexDirection: { xs: 'column', sm: 'row' } }}>
                <Typography
                  variant="body1"
                  sx={{
                    minWidth: { xs: '100%', sm: 150 },
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    fontWeight: 'bold',
                    mb: { xs: 1, sm: 0 },
                  }}
                >
                  Source:
                </Typography>
                {query?.queryURL ? (
                  <Typography variant="body1" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    <Link
                      href={query.queryURL}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ marginRight: '10px', display: 'flex', alignItems: 'center' }}
                    >
                      {getLinkType(query?.queryURL)}
                      <OpenInNew sx={{ fontSize: '1rem', ml: 1 }} />
                    </Link>
                  </Typography>
                ) : (
                  <Typography variant="body1" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    {QUERY_SOURCES[(query?.source || '').toLowerCase()] || query?.source || 'Other'}
                  </Typography>
                )}
              </Box>

              <Divider sx={{ my: 2, borderColor: 'rgba(0, 0, 0, 0.1)' }} />

              {query.status !== 'new' && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      fontWeight: 'bold',
                      mb: 1,
                    }}
                  >
                    Assign to:
                  </Typography>
                  <UserSelect users={users} selectedUserId={query?.userClaimID} query={query} mutate={refetch}/>
                </Box>
              )}

              <ClaimButton query={query} claimedUser={claimedUser} mutate={refetch} handleDiscardQuery={handleDiscardQuery} hideAvatar={true} />
            </>
          )}
        </CardContent>
      </Card>

      <QueryFormModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        query={query}
        onSuccess={() => {
          setEditModalOpen(false);
          refetch();
        }}
      />
    </>
  );
};

QueryDetailsCard.propTypes = {
  query: PropTypes.object.isRequired,
  handleDiscardQuery: PropTypes.func.isRequired,
  refetch: PropTypes.func.isRequired,
};

export default QueryDetailsCard;
